import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../../pages/admin/ui_engine";
import { AdminButton } from "../../../pages/admin/action_engine";
import { useAdminScope } from "../../../context/AdminScopeContext";
import {
  DEFAULT_SUPPLIER_CONTRACT_LIMIT,
  getSupplierContractErrorMessage,
  listSupplierContracts,
} from "../../../services/supplierContractsAdminApi";
import SupplierCommercialRulesAdminPanel from "../SupplierCommercialRulesAdminPanel";

import SupplierContractsList from "./SupplierContractsList";

import {
  AdminFilterSelect,
  AdminFormToolbar,
  AdminPagination,
  AdminSearchInput,
} from "../../../pages/admin/section_engine";

export const SUPPLIER_CONTRACT_WORKSPACE_STATUS =
  Object.freeze({
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });

const EMPTY_PAGINATION = Object.freeze({
  offset: 0,
  limit: DEFAULT_SUPPLIER_CONTRACT_LIMIT,
  total: 0,
});

function normalizeResponse(payload) {
  return {
    contracts: Array.isArray(payload?.items)
      ? payload.items
      : [],
    pagination: {
      ...EMPTY_PAGINATION,
      ...(payload?.pagination || {}),
    },
  };
}

function resolveInitialSelection(
  contracts,
  previousContractUuid = "",
) {
  if (!contracts.length) {
    return null;
  }

  const previousSelection = contracts.find(
    (contract) => (
      contract.uuid === previousContractUuid
    ),
  );

  if (previousSelection) {
    return previousSelection;
  }

  return (
    contracts.find(
      (contract) => contract.is_default,
    )
    || contracts[0]
  );
}

export default function SupplierContractWorkspace({
  supplierUuid,
  supplierLabel = "",
}) {
  const {
    selectedCompanyUuid,
  } = useAdminScope();

  const [status, setStatus] = useState(
    SUPPLIER_CONTRACT_WORKSPACE_STATUS.LOADING,
  );
  const [contracts, setContracts] = useState([]);
  const [pagination, setPagination] = useState(
    EMPTY_PAGINATION,
  );
  const [
    selectedContract,
    setSelectedContract,
  ] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const loadContracts = useCallback(
    async ({
      requestedOffset = 0,
      requestedSearch = search,
      requestedActive = activeFilter,
      preserveData = false,
    } = {}) => {
      if (
        typeof supplierUuid !== "string"
        || !supplierUuid.trim()
      ) {
        setContracts([]);
        setPagination(EMPTY_PAGINATION);
        setSelectedContract(null);
        setErrorMessage(
          "Aucun fournisseur n’est sélectionné.",
        );
        setStatus(
          SUPPLIER_CONTRACT_WORKSPACE_STATUS.ERROR,
        );
        return;
      }

      if (preserveData) {
        setRefreshing(true);
      } else {
        setStatus(
          SUPPLIER_CONTRACT_WORKSPACE_STATUS.LOADING,
        );
      }

      setErrorMessage("");

      try {
        const payload = await listSupplierContracts(
          supplierUuid,
          {
            companyUuid: selectedCompanyUuid,
            offset: requestedOffset,
            limit: DEFAULT_SUPPLIER_CONTRACT_LIMIT,
            search: requestedSearch,
            active:
              requestedActive === ""
                ? null
                : requestedActive === "active",
          },
        );

        const normalized = normalizeResponse(
          payload,
        );

        setContracts(normalized.contracts);
        setPagination(normalized.pagination);

        setSelectedContract((current) => (
          resolveInitialSelection(
            normalized.contracts,
            current?.uuid,
          )
        ));

        setStatus(
          normalized.contracts.length
            ? SUPPLIER_CONTRACT_WORKSPACE_STATUS.READY
            : SUPPLIER_CONTRACT_WORKSPACE_STATUS.EMPTY,
        );
      } catch (error) {
        if (!preserveData) {
          setContracts([]);
          setPagination(EMPTY_PAGINATION);
          setSelectedContract(null);
          setStatus(
            SUPPLIER_CONTRACT_WORKSPACE_STATUS.ERROR,
          );
        }

        setErrorMessage(
          getSupplierContractErrorMessage(
            error,
            "Impossible de charger les contrats fournisseur.",
          ),
        );
      } finally {
        setRefreshing(false);
      }
    },
    [
      activeFilter,
      search,
      selectedCompanyUuid,
      supplierUuid,
    ],
  );

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const normalized = searchInput.trim();

    if (normalized === search) {
      loadContracts({
        requestedOffset: 0,
        requestedSearch: normalized,
        requestedActive: activeFilter,
      });
      return;
    }

    setSearch(normalized);
  };

  const handleResetSearch = () => {
    setSearchInput("");

    if (!search) {
      loadContracts({
        requestedOffset: 0,
        requestedSearch: "",
        requestedActive: activeFilter,
      });
      return;
    }

    setSearch("");
  };

  const handleActiveFilterChange = (event) => {
    setActiveFilter(event.target.value);
  };

  useEffect(() => {
    loadContracts({
      requestedOffset: 0,
      requestedSearch: search,
      requestedActive: activeFilter,
    });
  }, [
    activeFilter,
    loadContracts,
    search,
  ]);

  const refresh = () => {
    loadContracts({
      requestedOffset: pagination.offset,
      requestedSearch: search,
      requestedActive: activeFilter,
      preserveData: contracts.length > 0,
    });
  };

  const hasPrevious = pagination.offset > 0;
  const hasNext =
    pagination.offset + pagination.limit
    < pagination.total;

  const currentPage = Math.floor(
    pagination.offset / pagination.limit,
  ) + 1;

  const totalPages = Math.max(
    Math.ceil(
      pagination.total / pagination.limit,
    ),
    1,
  );

  const selectedContractLabel = useMemo(
    () => selectedContract?.contract_name || "",
    [selectedContract],
  );

  return (
    <WorkspaceRoot>
      <WorkspaceHeader>
        <div>
          <WorkspaceEyebrow>
            Contrats fournisseur
          </WorkspaceEyebrow>

          <WorkspaceTitle>
            {supplierLabel || "Fournisseur sélectionné"}
          </WorkspaceTitle>
        </div>

        <AdminButton
          type="button"
          variant="secondary"
          onClick={refresh}
          disabled={
            status
              === SUPPLIER_CONTRACT_WORKSPACE_STATUS.LOADING
            || refreshing
          }
        >
          {refreshing
            ? "Actualisation..."
            : "Actualiser"}
        </AdminButton>
      </WorkspaceHeader>

      <AdminFormToolbar onSubmit={handleSearchSubmit} $inline>
        <AdminSearchInput
          type="search"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
          placeholder="Rechercher un contrat"
          aria-label="Rechercher un contrat fournisseur"
        />

        <AdminFilterSelect
          value={activeFilter}
          onChange={handleActiveFilterChange}
          aria-label="Filtrer les contrats par statut"
        >
          <option value="">
            Tous les statuts
          </option>
          <option value="active">
            Contrats actifs
          </option>
          <option value="inactive">
            Contrats inactifs
          </option>
        </AdminFilterSelect>

        <AdminButton
          type="submit"
          variant="primary"
        >
          Rechercher
        </AdminButton>

        {(search || searchInput) && (
          <AdminButton
            type="button"
            variant="secondary"
            onClick={handleResetSearch}
          >
            Réinitialiser
          </AdminButton>
        )}
      </AdminFormToolbar>

      {status
        === SUPPLIER_CONTRACT_WORKSPACE_STATUS.LOADING && (
        <AdminLoadingState
          message="Chargement des contrats fournisseur..."
        />
      )}

      {status
        === SUPPLIER_CONTRACT_WORKSPACE_STATUS.ERROR && (
        <AdminErrorState
          message={errorMessage}
          compact
        />
      )}

      {status
        === SUPPLIER_CONTRACT_WORKSPACE_STATUS.EMPTY && (
        <AdminEmptyState
          message={
            search || activeFilter
              ? "Aucun contrat ne correspond aux filtres sélectionnés."
              : "Aucun contrat n’est configuré pour ce fournisseur."
          }
        />
      )}

      {status
        === SUPPLIER_CONTRACT_WORKSPACE_STATUS.READY && (
        <WorkspaceContent>
          <ContractsColumn>
            <Summary>
              <strong>
                {pagination.total}
              </strong>{" "}
              contrat
              {pagination.total > 1 ? "s" : ""}
              {refreshing
                ? " — actualisation…"
                : ""}
            </Summary>

            <SupplierContractsList
              contracts={contracts}
              selectedContractUuid={
                selectedContract?.uuid || ""
              }
              onSelectContract={
                setSelectedContract
              }
            />

            <AdminPagination
              previousDisabled={
                !hasPrevious
                || refreshing
              }
              nextDisabled={
                !hasNext
                || refreshing
              }
              onPrevious={() => {
                loadContracts({
                  requestedOffset: Math.max(
                    pagination.offset
                      - pagination.limit,
                    0,
                  ),
                  requestedSearch: search,
                  requestedActive: activeFilter,
                });
              }}
              onNext={() => {
                loadContracts({
                  requestedOffset:
                    pagination.offset
                    + pagination.limit,
                  requestedSearch: search,
                  requestedActive: activeFilter,
                });
              }}
              label={`Page ${currentPage} / ${totalPages}`}
            />
          </ContractsColumn>

          <RulesColumn>
            {selectedContract ? (
              <SupplierCommercialRulesAdminPanel
                contractUuid={
                  selectedContract.uuid
                }
                contractLabel={
                  selectedContractLabel
                }
                supplierLabel={
                  supplierLabel
                }
              />
            ) : (
              <AdminEmptyState
                message="Sélectionnez un contrat pour afficher ses règles commerciales."
                compact
              />
            )}
          </RulesColumn>
        </WorkspaceContent>
      )}
    </WorkspaceRoot>
  );
}

const WorkspaceRoot = styled.section`
  display: grid;
  gap: 1rem;
  min-width: 0;
  color: #f7f7f7;
`;

const WorkspaceHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const WorkspaceEyebrow = styled.p`
  margin: 0 0 0.3rem;
  color: rgba(247, 247, 247, 0.55);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const WorkspaceTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const WorkspaceContent = styled.div`
  display: grid;
  grid-template-columns:
    minmax(280px, 0.85fr)
    minmax(360px, 1.4fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ContractsColumn = styled.section`
  min-width: 0;
`;

const RulesColumn = styled.section`
  min-width: 0;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
`;

const Summary = styled.p`
  margin: 0 0 0.75rem;
  color: rgba(247, 247, 247, 0.68);
`;
